<template>
  <div
    :class="{
      'form-popup-overlay': true,
      'is-visible': isVisible,
      'px-5': true
    }"
    @click="hide">
    <div
      :class="{
        'form-popup': true,
        'is-big': mode === 'view',
        'px-5': true,
        'pb-4': true,
        'pt-4': true,
        'is-loading': loading
      }"
      @click.stop>
      <h3 class="text-center d-flex mb-4">
        <span>
          {{ popupTitle }}
          <small
            v-if="mode === 'view' && !loadError"
            class="text-small">
            for UTC at <strong>{{ data.date }}</strong> from <strong>{{ data.time_from }}</strong> to <strong>{{ data.time_to }}</strong>
          </small>
        </span>

        <a
          href="#"
          @click.prevent="cancel()"
          class="text-danger ml-auto"
          style="text-decoration: none!important; font-size: 48px;">
          &times;
        </a>
      </h3>

      <d-alert
        :show="loadError"
        theme="warning">
        An error occurred during loading slot data...
      </d-alert>

      <validation-observer
        v-if="mode !== 'view' && !loadError"
        ref="monitoring-form" tag="form">
        <d-row>
          <div class="form-group w-100">
            <validation-provider
              rules="required"
              name="icao_type"
              v-slot="{ errors }">
              <label for="notes">ICAO type:</label>
              <d-input
                v-model="data.icao_type"
                :state="errors.length ? 'invalid' : null" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </d-row>

        <d-row>
          <d-col cols="6">
            <div class="form-group w-100">
              <validation-provider
                rules="required"
                name="time_from"
                v-slot="{ errors }">
                <label for="date">Slot start time:</label>
                <d-input
                  v-model="data.time_from"
                  type="time"
                  :state="errors.length ? 'invalid' : null" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </d-col>

          <d-col cols="6">
            <div class="form-group w-100">
              <validation-provider
                rules="required"
                name="time_to"
                v-slot="{ errors }">
                <label for="date">Slot end time:</label>
                <d-input
                  v-model="data.time_to"
                  type="time"
                  :state="errors.length ? 'invalid' : null" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </d-col>
        </d-row>

        <d-row>
          <div class="form-group w-100">
            <validation-provider
              rules="required"
              name="date"
              v-slot="{ errors }">
              <label for="date">Date:</label>
              <d-input
                v-model="data.date"
                type="date"
                pattern="\d{4}-\d{2}-\d{2}"
                :state="errors.length ? 'invalid' : null" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </d-row>

        <d-row>
          <div class="form-group w-100">
            <label for="notes">Registration:</label>
            <d-input
              v-model="data.registration" />
          </div>
        </d-row>
      </validation-observer>

      <template v-if="mode === 'view' && !loadError">
        <d-row v-if="availableSlots.length">
          <d-col cols="12">
            Status: <div class="AvDft" style="width: 12px; height: 12px; margin: 0 5px; display: inline-block;"></div> Arr/Dep Slots
          </d-col>
        </d-row>
        <d-row v-else>
          <d-col cols="12">
            Status: <div class="AvNoSlot" style="width: 12px; height: 12px; margin: 0 5px; display: inline-block;"></div> No Slots
          </d-col>
        </d-row>

        <div class="my-3" v-if="availableSlots.length">
          <d-row
            v-for="(row, index) of availableSlots"
            :key="'observed-slot-row-' + index"
            class="mx-0 my-1">
            <small>
              Slots available:
              <strong class="text-available">{{ row.value }}</strong> at
              <strong class="text-available">{{ row.hour }}:{{ row.minutes.split(' - ')[0] }} - {{ row.hour }}:{{ row.minutes.split(' - ')[1] }}</strong>
            </small>
          </d-row>
        </div>
        <div class="my-3" v-else>
          <d-row class="mx-0 my-1">
            <small>
              Slots available: 0
            </small>
          </d-row>
        </div>
      </template>

      <d-row
        v-if="mode === 'view' && !loadError"
        class="mx-0">
        <p class="text-center">
          All times in <strong><u>UTC</u></strong> - {{ data.date }} - Reg: {{ data.registration }}
        </p>

        <table class="w-100 table table-border text-center mb-4">
          <thead>
            <tr>
              <th></th>
              <th v-for="column in uniqueColumns" :key="column">{{ column }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(rowValues, row) in viewData"
              :key="'row-' + row">
              <td>{{ row }}</td>
              <td
                v-for="column in uniqueColumns"
                :key="'cell-' + row + '-' + column"
                :class="getCellClass(row, column)">
                {{ getCellValue(row, column) }}
              </td>
            </tr>
          </tbody>
        </table>

        <dl class="legend-list">
          <dt class="legend-item legend-AvDft"></dt>
          <dd>Arr/Dep Slots</dd>
          <dt class="legend-item legend-AvNoSlot"></dt>
          <dd>No Slots</dd>
          <dt class="legend-item legend-AvDep"></dt>
          <dd>Dep only</dd>
          <dt class="legend-item legend-AvArr"></dt>
          <dd>Arr only</dd>
          <dt class="legend-item legend-AvAllLockedAllAccessible"></dt>
          <dd>Locked Arr/Dep</dd>
          <dt class="legend-item legend-AvAllLockedDepAccessible"></dt>
          <dd>Locked Dep</dd>
        </dl>
      </d-row>

      <d-row v-if="mode !== 'view' && !loadError">
        <div class="text-center w-100">
          <d-button
            @click.prevent="validateForm"
            theme="success"
            class="mr-5">
            Save
          </d-button>

          <d-button
            @click.prevent="cancel"
            outline
            theme="secondary">
            Cancel
          </d-button>
        </div>
      </d-row>
    </div>
  </div>
</template>

<script>
import FormUtils from './../../utils/FormUtils';
import Vue from 'vue';

export default {
  name: 'MonitoringPopup',
  computed: {
    popupTitle () {
      if (this.mode === 'add') {
        return 'Add observed slot';
      } else if (this.mode === 'view') {
        return 'Slot details';
      }

      return 'Edit observed slot';
    },
    uniqueColumns () {
      if (this.data.values) {
        return [...new Set(this.data.values.map(item => item.column))];
      }

      return [];
    },
    availableSlots () {
      let availableSlots = this.data.observed_hours.filter(hour => hour.html_class.indexOf('AvDft') > -1);
      return availableSlots;
    }
  },
  data () {
    return {
      isVisible: false,
      mode: 'add',
      loading: false,
      loadError: false,
      data: {
        id: false,
        icao_type: '',
        registration: '',
        date: '',
        time_from: '',
        time_to: '',
        values: [],
        observed_hours: []
      },
      viewData: {}
    };
  },
  mounted () {
    this.$bus.$on('show-monitoring-popup', this.show);
  },
  methods: {
    show (mode, slotId = false) {
      this.mode = mode;

      this.data = {
        id: false,
        icao_type: '',
        registration: '',
        date: '',
        time_from: '',
        time_to: '',
        values: [],
        observed_hours: []
      };

      if (mode === 'add') {
        this.loading = false;
      } else {
        this.loading = true;
        this.loadSlotData(slotId);
      }

      setTimeout(() => {
        if (this.mode !== 'view') {
          this.$refs['monitoring-form'].reset();
        }
        this.isVisible = true;
      }, 100);
    },
    loadSlotData (warningId) {
      FormUtils.loadItemData(this, {
        id: warningId,
        endpointBase: '/geneva/slots/item/',
        instanceFields: ['id', 'icao_type', 'date', 'time_from', 'time_to', 'registration', 'observed_hours', 'values'],
        loadedKey: 'loaded',
        failAction: () => {
          this.loading = false;
        },
        successAction: (response) => {
          this.loading = false;

          if (this.mode === 'view') {
            Vue.set(this.data, 'values', response.values);
            Vue.set(this, 'viewData', {});

            this.data.values.forEach(item => {
              if (!this.viewData[item.row]) {
                Vue.set(this.viewData, item.row, []);
              }

              this.viewData[item.row].push({
                column: item.column,
                value: item.value,
                html_class: item.html_class
              });
            });
          } else {
            this.$refs['monitoring-form'].reset();
          }
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['monitoring-form'], this.submitForm);
    },
    submitForm () {
      this.loading = true;
      let endpointUrl = '/geneva/slots/store';

      if (this.mode === 'edit') {
        endpointUrl = '/geneva/slots/update/' + this.data.id;
      }

      let formDataToSend = this.prepareFormData();

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.mode === 'add' ? 'Observed slot added' : 'Observed slot saved',
        successText: '',
        errorTitle: 'An error occurred',
        errorText: 'The system was unable to save the observed slot. Please try again.',
        successAction: () => {
          this.loading = false;
          this.isVisible = false;
          this.$bus.$emit('monitoring-list-reload');
          this.$bus.$emit('topbar-get-notifications');
        },
        failAction: () => {
          this.loading = false;
          this.isVisible = false;
          this.$bus.$emit('monitoring-list-reload');
          this.$bus.$emit('topbar-get-notifications');
        }
      });
    },
    cancel () {
      this.isVisible = false;
      this.loading = false;
      this.loadError = false;
    },
    hide () {
      this.isVisible = false;
      this.loading = false;
      this.loadError = false;
    },
    prepareFormData () {
      let dataToSend = {
        icao_type: this.data.icao_type,
        registration: this.data.registration,
        date: this.data.date,
        time_from: this.data.time_from,
        time_to: this.data.time_to
      };

      return dataToSend;
    },
    getCellClass(row, column) {
      let cell = this.viewData[row].find(c => c.column === column);
      return cell ? cell.html_class : '';
    },
    getCellValue(row, column) {
      let cell = this.viewData[row].find(c => c.column === column);
      return cell ? cell.value : '';
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-monitoring-popup', this.show);
  }
}
</script>

<style lang="scss">
.legend-AvNoSlot,
.AvNoSlot {
  background: red;
  color: #fff;
}

.text-available {
  color: green;
}

.legend-AvDft,
.AvDft {
  background: green;
  color: #fff;
}

.legend-AvDep,
.AvDep {
  background: orange;
  color: #fff;
}

.legend-AvArr,
.AvArr {
  background: violet;
  color: #fff;
}

.legend-AvAllLockedAllAccessible,
.AvAllLockedAllAccessible {
  background: blue;
  color: #fff;
}

.legend-AvAllLockedDepAccessible,
.AvAllLockedDepAccessible {
  background: darkblue;
  color: #fff;
}

.legend-list {
  display: flex;

  .legend-item {
    height: 24px;
    width: 24px;
  }

  dd {
    line-height: 24px;
    padding: 0 10px;
  }
}
</style>
